//@ts-strict-ignore
import { usePrecision } from '../../utils/hooks';
import { useSymbolFromCurrency } from '../../utils/CurrencyUtil';
import BlurTextField from '../connectedForm/BlurTextField';
import React, { useContext, useEffect, useState } from 'react';
import FluentTextField, { IFluentTextFieldProps } from '../FluentWrapper/FluentTextField';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { useDispatch } from 'react-redux';
import { CostPriceOrigin, Maybe } from '../../graphql/schema/p4types';
import { useDebouncedTemporarySave } from '../connectedForm/utils/handleTemporarySave';
import getIn from '../connectedForm/utils/getIn';
import { IInputLabelProps } from '../connectedForm/InputLabel';

export type IPriceDescriptionEditableFieldProps = IFluentTextFieldProps & {
  lineDataid: string;
  unitQuantity: number;
  unitDescription: Maybe<string>;
  valueType?: 'sales' | 'cost';
};

export const PriceDescriptionEditableField = ({
  lineDataid,
  unitQuantity,
  unitDescription,
  valueType,
  ...rest
}: IPriceDescriptionEditableFieldProps) => {
  const dispatch = useDispatch();
  const connectedForm = useContext(ConnectedFormContext);

  const performTemporarySave = useDebouncedTemporarySave(false, 300);

  const displayCostPrice = getIn(connectedForm.values, `${lineDataid}.displayCostPrice`);
  const [costPriceValue, setCostPriceValue] = useState(displayCostPrice);

  const precision = usePrecision(valueType);

  useEffect(() => {
    const costPriceNumber = Number(displayCostPrice);
    if (costPriceNumber) {
      setCostPriceValue(costPriceNumber.toFixed(precision));
    }
  }, [displayCostPrice]);

  const onBlur = (value: any) => {
    if (displayCostPrice === Number(value)) {
      return;
    }

    Promise.all([
      dispatch(connectedForm.change(`${lineDataid}.costPriceOrigin`, CostPriceOrigin.Manual)),
      dispatch(connectedForm.change(`${lineDataid}.updatedCostPrice`, value ?? 0)),
      dispatch(connectedForm.change(`${lineDataid}.costPrice`, value ?? 0)),
      dispatch(connectedForm.change(`${lineDataid}.displayCostPrice`, value ?? 0)),
    ]).then(performTemporarySave);
  };

  return (
    <FluentTextField
      {...rest}
      suffix={` per ${unitQuantity} (${unitDescription})`}
      onBlur={onBlur}
      value={costPriceValue}
      onFocus={(event) => {
        event.target.select();
      }}
    />
  );
};

/**
 * Displays a price including the the 'per each' notation (e.g. '£5 per 3 (box)).
 */
export const PriceDescriptionField = ({
  label,
  price,
  unitQuantity,
  unitDescription,
  currencyCode,
  labelCols,
  inputCols,
  type,
  valueType,
  labelProps,
  testId,
}: {
  label: string;
  price: number | string;
  unitQuantity: number;
  unitDescription: Maybe<string>;
  currencyCode: Maybe<string>;
  labelCols?: number;
  inputCols?: number;
  type?: 'currency' | 'baseCurrency';
  valueType?: string;
  labelProps?: IInputLabelProps;
  testId?: string;
}) => {
  const precision = usePrecision(valueType);

  let priceFormatted: string = price as string;
  if (price && (price as number).toFixed) {
    priceFormatted = (price as number).toFixed(precision);
  }

  const currencySymbol = useSymbolFromCurrency(type, priceFormatted);
  const labelAddon = currencyCode ? ` (${currencyCode})` : undefined;

  const costPriceDescription = `${currencySymbol}${
    currencySymbol && currencySymbol.length > 1 ? ' ' : ''
  }${priceFormatted} per ${unitQuantity} (${unitDescription})`;
  return (
    <BlurTextField
      readOnly={true}
      value={costPriceDescription}
      label={label}
      labelAddon={labelAddon}
      key={label}
      labelCols={labelCols}
      inputCols={inputCols}
      valueType={valueType}
      labelProps={labelProps}
      data-testid={testId}
    />
  );
};
